import { useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { MapPin, Phone, Mail, Globe, Star, Clock, Bookmark, BookMarked } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { Business, Review } from '@shared/schema';

export default function BusinessPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(5);

  const { data: business, isLoading } = useQuery({
    queryKey: ['/api/businesses', id],
    queryFn: () => fetch(`/api/businesses/${id}`).then(res => res.json()) as Promise<Business>,
    enabled: !!id
  });

  const { data: reviews = [] } = useQuery({
    queryKey: ['/api/businesses', id, 'reviews'],
    queryFn: () => fetch(`/api/businesses/${id}/reviews`).then(res => res.json()) as Promise<Review[]>,
    enabled: !!id
  });

  const saveBusinessMutation = useMutation({
    mutationFn: () => apiRequest(`/api/users/${user?.uid}/save-business`, {
      method: 'POST',
      body: JSON.stringify({ businessId: id })
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users', user?.uid] });
    }
  });

  const createReviewMutation = useMutation({
    mutationFn: (reviewData: { rating: number; comment: string }) => 
      apiRequest('/api/reviews', {
        method: 'POST',
        body: JSON.stringify({
          businessId: id,
          userId: user?.uid,
          userName: user?.displayName || 'Anonymous',
          userPhoto: user?.photoURL,
          ...reviewData
        })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/businesses', id, 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['/api/businesses', id] });
      setReviewText('');
      setRating(5);
    }
  });

  const handleSaveBusiness = () => {
    if (user) {
      saveBusinessMutation.mutate();
    }
  };

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (user && reviewText.trim()) {
      createReviewMutation.mutate({
        rating,
        comment: reviewText.trim()
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Business Not Found</h1>
          <p className="text-gray-600">The business you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{business.name}</h1>
              <p className="text-xl mb-4 text-green-100">{business.description}</p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <span className="px-3 py-1 bg-red-800 rounded-full text-sm">
                  {business.category}
                </span>
                {business.rating > 0 && (
                  <div className="flex items-center bg-fba-red text-black px-3 py-1 rounded-full">
                    <Star size={16} className="mr-1" />
                    <span className="font-semibold">{business.rating.toFixed(1)}</span>
                    <span className="text-sm ml-1">({business.reviewCount} reviews)</span>
                  </div>
                )}
              </div>
              
              {user && (
                <button
                  onClick={handleSaveBusiness}
                  disabled={saveBusinessMutation.isPending}
                  className="flex items-center space-x-2 px-4 py-2 bg-white text-red-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {saveBusinessMutation.isPending ? (
                    <BookMarked size={20} />
                  ) : (
                    <Bookmark size={20} />
                  )}
                  <span>Save Business</span>
                </button>
              )}
            </div>
            
            <div className="w-full lg:w-80">
              <div className="bg-white text-gray-900 rounded-lg p-6">
                <h3 className="font-bold text-lg mb-4">Contact Information</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <MapPin size={20} className="text-red-700 mt-1" />
                    <div>
                      <p className="font-medium">{business.address}</p>
                      <p className="text-gray-600">{business.city}, {business.state} {business.zipCode}</p>
                    </div>
                  </div>
                  
                  {business.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone size={20} className="text-red-700" />
                      <a href={`tel:${business.phone}`} className="text-blue-600 hover:underline">
                        {business.phone}
                      </a>
                    </div>
                  )}
                  
                  {business.email && (
                    <div className="flex items-center space-x-3">
                      <Mail size={20} className="text-red-700" />
                      <a href={`mailto:${business.email}`} className="text-blue-600 hover:underline">
                        {business.email}
                      </a>
                    </div>
                  )}
                  
                  {business.website && (
                    <div className="flex items-center space-x-3">
                      <Globe size={20} className="text-red-700" />
                      <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Visit Website
                      </a>
                    </div>
                  )}
                  
                  {business.hours && (
                    <div className="flex items-start space-x-3">
                      <Clock size={20} className="text-red-700 mt-1" />
                      <div>
                        <p className="font-medium">Hours</p>
                        <p className="text-gray-600">{business.hours}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Reviews</h2>
          
          {/* Add Review Form */}
          {user && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Write a Review</h3>
              <form onSubmit={handleSubmitReview}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating
                  </label>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className={`text-2xl ${star <= rating ? 'text-yellow-500' : 'text-gray-300'}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Review
                  </label>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    rows={4}
                    placeholder="Share your experience..."
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={createReviewMutation.isPending || !reviewText.trim()}
                  className="px-6 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-400 transition-colors"
                >
                  {createReviewMutation.isPending ? 'Submitting...' : 'Submit Review'}
                </button>
              </form>
            </div>
          )}
          
          {/* Reviews List */}
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start space-x-4">
                  {review.userPhoto && (
                    <img
                      src={review.userPhoto}
                      alt={review.userName}
                      className="w-12 h-12 rounded-full"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className={`text-sm ${i < review.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700">{review.comment}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {reviews.length === 0 && (
              <div className="text-center py-12">
                <Star size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Reviews Yet</h3>
                <p className="text-gray-500">Be the first to review this business!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}